import {Form, Input, InputNumber, Modal, Select, message} from 'antd';
import PropTypes from 'prop-types';
import React, {useEffect} from 'react';

import {RqRegisterFinalProduct} from '../../../../../reactQuery/apis';

const PRIMARY       = '#4f46e5';
const PRIMARY_LIGHT = '#eef2ff';

// ─── Section Title ─────────────────────────────────────────────────────────────
const SectionTitle = ({children}) => (
  <div className={'flex items-center gap-2 mb-4'}>
    <div className={'w-1 h-5 rounded-full'} style={{background: PRIMARY}} />
    <span className={'font-bold text-slate-700 uppercase tracking-widest text-xs'}>
      {children}
    </span>
  </div>
);
SectionTitle.propTypes = {children: PropTypes.node.isRequired};

// Shared rule factories
const noWhitespace = {
  validator: (_, value) =>
    value && String(value).trim() === ''
      ? Promise.reject('This field cannot be empty or contain only spaces')
      : Promise.resolve(),
};

const required = label => [
  {required: true, message: `${label} is required`},
  noWhitespace,
];

const requiredNumber = label => [
  {required: true, message: `${label} is required`},
  {type: 'number', min: 0, max: 100, message: `${label} must be between 0 and 100`},
];

const AddProduct = ({open, onClose, token}) => {
  const [form] = Form.useForm();
  const {mutateAsync, isPending} = RqRegisterFinalProduct();

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const payload = {
        ...values,
        cgst: Number(values.cgst || 0),
        sgst: Number(values.sgst || 0),
        igst: Number(values.igst || 0),
        tax: {
          cgst: Number(values.cgst || 0),
          sgst: Number(values.sgst || 0),
          igst: Number(values.igst || 0),
        },
        category: 'KUMKUM',
      };

      await mutateAsync({payload, token});
      message.success('Product registered successfully');
      form.resetFields(); 
      onClose();
    } catch (err) {
      if (err?.errorFields) {return;}
      message.error(err?.message || 'Registration failed');
    }
  };

  // Watch cgst and sgst values dynamically to auto-update igst
  const cgstValue = Form.useWatch('cgst', form);
  const sgstValue = Form.useWatch('sgst', form);

  // Automatically update igst when cgst or sgst changes
  useEffect(() => {
    const sum = Number((Number(cgstValue || 0) + Number(sgstValue || 0)).toFixed(2));
    form.setFieldsValue({igst: sum});
  }, [cgstValue, sgstValue, form]);
  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={(
        <div className={'flex items-center gap-3 py-1'}>
          <div
            className={'w-8 h-8 rounded-md flex items-center justify-center text-white text-sm font-bold'}
            style={{background: 'linear-gradient(135deg, #1a3550)'}}
          >
            +
          </div>
          <div>
            <div className={'font-bold text-slate-800 text-base'}>Register New Product</div>
            <div className={'text-slate-400 font-normal text-sm'}>All fields are required</div>
          </div>
        </div>
      )}
      open={open}
      onCancel={handleCancel}
      onOk={handleSubmit}
      confirmLoading={isPending}
      okText={'Register'}
      okButtonProps={{className: 'btn-primary px-6', loading: isPending}}
      cancelButtonProps={{className: 'rounded-md'}}
      width={700}
      destroyOnClose
      styles={{
        header: {borderBottom: `3px solid ${PRIMARY_LIGHT}`, paddingBottom: 16},
        body:   {padding: '20px 24px'},
      }}
    >
      <Form
        form={form}
        layout={'vertical'}
        initialValues={{
          status: 'ACTIVE',
          cgst: 0,
          sgst: 0,
          igst: 0,
          category: 'KUMKUM',
        }}
      >
        {/* ── Basic Information ── */}
        <div className={'rounded-md p-4 mb-4 bg-primary-50 border border-primary-100'}>
          <SectionTitle>Basic Information</SectionTitle>

          <div className={'grid grid-cols-2 gap-x-3'}>

            <Form.Item
              name={'productName'}
              label={<span className={'font-semibold'}>Product Name</span>}
              rules={required('Product name')}
              normalize={v => v.trim()}
              className={'col-span-2'}
            >
              <Input
                className={'rounded-md'}
                placeholder={'e.g. Gulal Red'}
                onChange={e => {
                  form.setFieldsValue({
                    productName: e.target.value.toUpperCase(),
                  });
                }}
              />
            </Form.Item>

            <Form.Item
              name={'description'}
              label={<span className={'font-semibold'}>Description</span>}
              rules={required('Description')}
              normalize={v => v.trim()}
              className={'col-span-2'}
            >
              <Input.TextArea
                className={'rounded-md'}
                rows={3}
                placeholder={'Short description'}
              />
            </Form.Item>

            <Form.Item
              name={'brand'}
              label={<span className={'font-semibold'}>Brand</span>}
              rules={required('Brand')}
              normalize={v => v.trim()}
            >
              <Input className={'rounded-md'} placeholder={'Brand name'} />
            </Form.Item>

            <Form.Item
              name={'category'}
              label={<span className={'font-semibold'}>Category</span>}
              rules={required('Category')}
            >
              <Input
                disabled
                className={'rounded-md'}
              />
            </Form.Item>

            <Form.Item
              name={'hsnCode'}
              label={'HSN Code'}
              rules={[
                {pattern: /^\d{1,13}$/, message: 'HSN Code must be 1–13 digits, numbers only'},
              ]}
            >
              <Input
                placeholder={'e.g. 12345678'}
                maxLength={13}
                onKeyDown={e => {
                  const allowed = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Enter'];
                  if (!allowed.includes(e.key) && !(e.ctrlKey || e.metaKey) && !/^\d$/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
              />
            </Form.Item>

            <Form.Item
              name={'status'}
              label={<span className={'font-semibold'}>Status</span>}
              rules={required('Status')}
              normalize={v => v.trim()}
              className={'col-span-2'}
            >
              <Select className={'rounded-md'} placeholder={'Select status'}>
                <Select.Option value={'ACTIVE'}>Active</Select.Option>
                <Select.Option value={'INACTIVE'}>Inactive</Select.Option>
              </Select>
            </Form.Item>

          </div>
        </div>

        {/* ── Tax Configuration ── */}
        <div className={'rounded-md p-4 mb-4 border border-primary-100'}>
          <SectionTitle>Tax Configuration</SectionTitle>

          <div className={'grid grid-cols-3 gap-x-3'}>
            <Form.Item
              name={'cgst'}
              label={<span className={'font-semibold'}>CGST (%)</span>}
              rules={requiredNumber('CGST')}
            >
              <InputNumber min={0} max={100} step={0.01} className={'w-full rounded-md'} placeholder={'0'} />
            </Form.Item>

            <Form.Item
              name={'sgst'}
              label={<span className={'font-semibold'}>SGST (%)</span>}
              rules={requiredNumber('SGST')}
            >
              <InputNumber min={0} max={100} step={0.01} className={'w-full rounded-md'} placeholder={'0'} />
            </Form.Item>

            <Form.Item
              name={'igst'}
              label={<span className={'font-semibold'}>IGST (%)</span>}
            >
              <InputNumber 
                disabled
                min={0} 
                max={100} 
                step={0.01} 
                className={'w-full rounded-md'} 
                placeholder={'0'} 
              />
            </Form.Item>
          </div>
        </div>

      </Form>
    </Modal>
  );
};

AddProduct.propTypes = {
  open:    PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  token:   PropTypes.string.isRequired,
};

export {AddProduct};
export default AddProduct;
